import { format, isValid, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';

/** Прочерк вместо пустого значения — единый placeholder во всех таблицах. */
export const EMPTY_VALUE = '—';

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;

export type DateLike = Date | string | number | null | undefined;

function toDate(value: DateLike): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const date = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(date) ? date : null;
}

function safeFormat(value: DateLike, pattern: string): string {
  const date = toDate(value);
  return date ? format(date, pattern, { locale: ru }) : EMPTY_VALUE;
}

/** 11.08.2026 */
export function formatDate(value: DateLike): string {
  return safeFormat(value, 'dd.MM.yyyy');
}

/** 11.08.2026 05:40 */
export function formatDateTime(value: DateLike): string {
  return safeFormat(value, 'dd.MM.yyyy HH:mm');
}

/** 05:40 */
export function formatTime(value: DateLike): string {
  return safeFormat(value, 'HH:mm');
}

/** 05:40:12 — для журналов интеграций и аудита. */
export function formatDateTimeSeconds(value: DateLike): string {
  return safeFormat(value, 'dd.MM.yyyy HH:mm:ss');
}

/** вторник, 11 августа 2026 — заголовок доски выпуска. */
export function formatDayLong(value: DateLike): string {
  return safeFormat(value, 'EEEE, d MMMM yyyy');
}

/** yyyy-MM-dd — значение для <input type="date"> и параметров запроса. */
export function toDateInput(value: DateLike): string {
  const date = toDate(value);
  return date ? format(date, 'yyyy-MM-dd') : '';
}

/** Сегодняшняя дата в формате параметра запроса. */
export function todayInput(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Минуты от полуночи → HH:mm. В расписании выходов время хранится как int,
 * значения ≥ 24:00 (ночные рейсы) заворачиваем в следующие сутки.
 */
export function minutesToHHmm(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) {
    return EMPTY_VALUE;
  }
  const normalized = ((Math.trunc(minutes) % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  const hours = Math.floor(normalized / MINUTES_IN_HOUR);
  const rest = normalized % MINUTES_IN_HOUR;
  return `${pad(hours)}:${pad(rest)}`;
}

/** HH:mm → минуты от полуночи; null, если строка не разбирается. */
export function hhmmToMinutes(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return hours * MINUTES_IN_HOUR + minutes;
}

/** 135 → «2 ч 15 мин» */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) {
    return EMPTY_VALUE;
  }
  const total = Math.max(0, Math.trunc(minutes));
  const hours = Math.floor(total / MINUTES_IN_HOUR);
  const rest = total % MINUTES_IN_HOUR;
  if (hours === 0) {
    return `${rest} мин`;
  }
  return rest === 0 ? `${hours} ч` : `${hours} ч ${rest} мин`;
}

export function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY_VALUE;
  }
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Одометр и пробег — целые километры. */
export function formatKm(value: number | null | undefined): string {
  const text = formatNumber(value);
  return text === EMPTY_VALUE ? text : `${text} км`;
}

/** Топливо — один знак после запятой (выдача/остаток в литрах). */
export function formatLiters(value: number | null | undefined): string {
  const text = formatNumber(value, 1);
  return text === EMPTY_VALUE ? text : `${text} л`;
}

/** «Иванов Иван Иванович» → «Иванов И. И.» — для узких колонок и мобильных. */
export function shortenFullName(fullName: string | null | undefined): string {
  if (!fullName) {
    return EMPTY_VALUE;
  }
  const [last, ...rest] = fullName.trim().split(/\s+/);
  if (rest.length === 0) {
    return last;
  }
  const initials = rest.map((part) => `${part.charAt(0).toUpperCase()}.`).join(' ');
  return `${last} ${initials}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
